import React, { useMemo } from 'react';
import styled from '@emotion/styled';

import {
  getColorForHueSaturation,
  getColorForTemperature,
  getDefaultColor,
  getMode,
} from '../../lib/capabilities/light';
import { round } from '../../lib/math';

import { useCapabilityValues } from '../../store/devices/useCapabilityValues';

export function IndicatorLight({ device }) {
  const [values] = useCapabilityValues({
    deviceId: device.id,
    capabilityIds: [
      'light_temperature',
      'light_hue',
      'light_saturation',
      'light_mode',
      'dim',
      'onoff',
    ],
  });

  const { color, borderColor } = useMemo(() => {
    return getColor({ values });
  }, [values]);

  return <IndicatorLightContainer color={color} borderColor={borderColor} />;
}

function getColor({ values }) {
  const dim = values.dim ?? 1;
  const scaledDim = logScale(dim);
  const borderDim = dim * 0.5;
  const light_mode = getMode(values);

  // Light that only has dim or no initial values
  if (
    values.light_temperature == null &&
    values.light_hue == null &&
    values.light_saturation == null
  ) {
    return {
      color: getDefaultColor().setAlpha(scaledDim).toHslString(),
      borderColor: getDefaultColor().setAlpha(borderDim).toHslString(),
    };
  }

  if (light_mode === 'color') {
    return {
      color: getColorForHueSaturation({
        light_hue: values.light_hue,
        light_saturation: values.light_saturation,
      })
        .setAlpha(scaledDim)
        .toHslString(),
      borderColor: getColorForHueSaturation({
        light_hue: values.light_hue,
        light_saturation: values.light_saturation,
      })
        .setAlpha(borderDim)
        .toHslString(),
    };
  }

  if (light_mode === 'temperature') {
    return {
      color: getColorForTemperature({
        light_temperature: values.light_temperature,
      })
        .setAlpha(scaledDim)
        .toHslString(),
      borderColor: getColorForTemperature({
        light_temperature: values.light_temperature,
      })
        .setAlpha(borderDim)
        .toHslString(),
    };
  }

  return {
    color: null,
    borderColor: null,
  };
}

const IndicatorLightContainer = styled.div`
  width: 16px;
  height: 16px;
  margin: 4px;
  border-radius: 50%;
  background-color: ${getBackgroundColor};
  box-shadow: 0 0 0 4px ${getBorderColor};
`;

function getBorderColor(props) {
  return props.borderColor ? props.borderColor : 'hsla(44,100%,72.7%,1)';
}

function getBackgroundColor(props) {
  return props.color ? props.color : 'hsla(44,100%,72.7%,1)';
}

function logScale(dim) {
  const minp = 0;
  const maxp = 1;

  // The result should be between 0.5 and 1
  const minv = Math.log(0.5);
  const maxv = Math.log(1);

  // calculate adjustment factor
  const scale = (maxv - minv) / (maxp - minp);

  return round(Math.exp(minv + scale * (dim - minp)), 2);
}
